import { useSerialStore } from '@/state/serialStore';
import { useSettingsStore } from '@/state/settingsStore';
import { SKYROVER_COLORS } from '@/theme/skyroverTheme';
import type { SerialLineEnding } from '@skyrover/shared/constants';
import { SERIAL_SUPPORTED_BAUDS } from '@skyrover/shared/constants';
import ClearIcon from '@mui/icons-material/Clear';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Checkbox,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

const LINE_ENDING_OPTIONS: { value: SerialLineEnding; label: string; title: string }[] = [
  { value: 'none', label: 'None', title: 'No line ending' },
  { value: 'nl', label: 'NL', title: 'Newline (\\n)' },
  { value: 'cr', label: 'CR', title: 'Carriage return (\\r)' },
  { value: 'crlf', label: 'NL+CR', title: 'Newline and carriage return' },
];

const toolbarIconSx = {
  color: SKYROVER_COLORS.brand,
  '&:hover': {
    bgcolor: SKYROVER_COLORS.brandMuted,
  },
  '&.Mui-disabled': {
    color: 'rgba(153, 0, 153, 0.35)',
  },
} as const;

const baudSelectSx = {
  minWidth: 76,
  height: 28,
  fontSize: 11,
  fontWeight: 600,
  '& .MuiSelect-select': {
    py: 0.25,
    px: 1,
    minHeight: 'unset !important',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: SKYROVER_COLORS.brand,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: SKYROVER_COLORS.brandLight,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: SKYROVER_COLORS.brand,
  },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(153, 0, 153, 0.35)',
  },
} as const;

const baudMenuProps = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': {
        fontSize: 11,
        py: 0.35,
        minHeight: 28,
      },
    },
  },
} as const;

const sendFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 12,
    '& fieldset': {
      borderColor: SKYROVER_COLORS.brand,
    },
    '&:hover fieldset': {
      borderColor: SKYROVER_COLORS.brandLight,
    },
    '&.Mui-focused fieldset': {
      borderColor: SKYROVER_COLORS.brand,
      borderWidth: 1,
    },
    '&.Mui-disabled fieldset': {
      borderColor: 'rgba(153, 0, 153, 0.35)',
    },
  },
} as const;

function formatTimestamp(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour12: false });
}

interface SerialMonitorPanelProps {
  compact?: boolean;
  isFullscreen?: boolean;
  onEnterFullscreen?: () => void;
  onExitFullscreen?: () => void;
}

function SerialMonitorPanel({
  compact = false,
  isFullscreen = false,
  onEnterFullscreen,
  onExitFullscreen,
}: SerialMonitorPanelProps) {
  const {
    connected,
    baudRate,
    autoScroll,
    showTimestamp,
    lineEnding,
    inputText,
    logs,
    webSerialSupported,
    setBaudRate,
    setAutoScroll,
    setShowTimestamp,
    setLineEnding,
    setInputText,
    send,
    clear,
  } = useSerialStore();

  const bottomPanelHeight = useSettingsStore((s) => s.bottomPanelHeight);
  const logEndRef = useRef<HTMLDivElement>(null);
  const panelHeight = isFullscreen ? '100%' : compact ? '100%' : bottomPanelHeight;
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (autoScroll) {
      logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const closeSettings = () => setSettingsAnchor(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: panelHeight, minHeight: compact || isFullscreen ? 0 : 160 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: isFullscreen ? '#fff' : undefined,
        }}
      >
        <FormControl size="small">
          <Select
            value={baudRate}
            onChange={(e) => setBaudRate(Number(e.target.value))}
            disabled={connected}
            aria-label="Baud rate"
            MenuProps={baudMenuProps}
            sx={baudSelectSx}
          >
            {SERIAL_SUPPORTED_BAUDS.map((b) => (
              <MenuItem key={b} value={b}>
                {b}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        {isFullscreen ? (
          <Tooltip title="Exit full screen">
            <IconButton size="small" onClick={onExitFullscreen} aria-label="Exit full screen" sx={toolbarIconSx}>
              <FullscreenExitIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Full screen">
            <IconButton size="small" onClick={onEnterFullscreen} aria-label="Full screen" sx={toolbarIconSx}>
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Serial monitor settings">
          <IconButton
            size="small"
            onClick={(e) => setSettingsAnchor(e.currentTarget)}
            aria-label="Serial monitor settings"
            sx={toolbarIconSx}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={settingsAnchor}
          open={Boolean(settingsAnchor)}
          onClose={closeSettings}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => setAutoScroll(!autoScroll)} dense sx={{ py: 0.5 }}>
            <Checkbox size="small" checked={autoScroll} sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Auto-scroll</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => setShowTimestamp(!showTimestamp)} dense sx={{ py: 0.5 }}>
            <Checkbox size="small" checked={showTimestamp} sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Timestamp</ListItemText>
          </MenuItem>
        </Menu>

        <Tooltip title="Clear output">
          <IconButton size="small" onClick={clear} aria-label="Clear serial output" sx={toolbarIconSx}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: 'action.hover',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 12,
          p: 1,
        }}
      >
        {logs.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {webSerialSupported
              ? 'Serial output will appear here after you connect to a board.'
              : 'Web Serial is not supported in this browser. Use Chrome or Edge to connect a board.'}
          </Typography>
        ) : (
          logs.map((entry) => (
            <Box key={entry.id} sx={{ display: 'flex', gap: 1, mb: 0.25 }}>
              {showTimestamp && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                  [{formatTimestamp(entry.timestamp)}]
                </Typography>
              )}
              <Typography
                component="span"
                variant="caption"
                sx={{
                  fontFamily: 'inherit',
                  color:
                    entry.direction === 'tx'
                      ? 'primary.main'
                      : entry.direction === 'system'
                        ? 'text.secondary'
                        : 'text.primary',
                }}
              >
                {entry.direction === 'tx' ? '→ ' : entry.direction === 'system' ? '• ' : ''}
                {entry.text}
              </Typography>
            </Box>
          ))
        )}
        <div ref={logEndRef} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: isFullscreen ? '#fff' : undefined,
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Send message…"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!connected}
          sx={sendFieldSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => void send()}
                  disabled={!connected || !inputText}
                  aria-label="Send"
                  sx={toolbarIconSx}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={lineEnding}
          onChange={(_, value: SerialLineEnding | null) => {
            if (value) {
              setLineEnding(value);
            }
          }}
          aria-label="Line ending"
          sx={{
            flexShrink: 0,
            '& .MuiToggleButton-root': {
              px: 0.75,
              py: 0.25,
              fontSize: 11,
              fontWeight: 600,
              lineHeight: 1.4,
              minWidth: 36,
            },
          }}
        >
          {LINE_ENDING_OPTIONS.map((option) => (
            <Tooltip key={option.value} title={option.title}>
              <ToggleButton value={option.value} aria-label={option.title}>
                {option.label}
              </ToggleButton>
            </Tooltip>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

export function SerialMonitor({ compact = false }: { compact?: boolean }) {
  const [fullscreen, setFullscreen] = useState(false);

  return (
    <>
      <SerialMonitorPanel compact={compact} onEnterFullscreen={() => setFullscreen(true)} />

      <Dialog fullScreen open={fullscreen} onClose={() => setFullscreen(false)}>
        <SerialMonitorPanel isFullscreen onExitFullscreen={() => setFullscreen(false)} />
      </Dialog>
    </>
  );
}
